export interface BusinessAuditRecommendation {
  id: number;
  title: string;
  description: string;
  category: string;
  priority: "Critical" | "High" | "Medium" | "Low";
  symptoms: string[];
  impact: string;
  quickWins: string[];
  auditBenefit: string;
  framework: string; // Which dimension of IN.8 Framework
}

export const businessAuditRecommendations: BusinessAuditRecommendation[] = [
  {
    id: 1,
    title: "Growth Stagnation Analysis",
    description: "Your business performance indicates potential hidden bottlenecks that are limiting growth potential and creating unnecessary friction.",
    category: "Business Performance",
    priority: "Critical",
    symptoms: [
      "Plateaued revenue for extended periods",
      "Increased effort required for same results",
      "Declining customer acquisition rates",
      "Reduced profit margins despite same activity levels"
    ],
    impact: "Without identifying root causes, 67% of businesses remain stuck at current performance levels for 2+ years",
    quickWins: [
      "Performance baseline establishment",
      "Growth bottleneck identification",
      "Resource allocation optimization"
    ],
    auditBenefit: "Our 3-Day Audit will reveal exactly which of the 8 business dimensions are creating your growth ceiling and provide a clear 30-day action plan.",
    framework: "Performance & Optimization"
  },
  {
    id: 2,
    title: "Marketing ROI Optimization",
    description: "Unclear marketing effectiveness suggests you may be wasting budget on activities that don't drive measurable business results.",
    category: "Marketing & Sales",
    priority: "Critical",
    symptoms: [
      "Unable to prove marketing ROI",
      "Inconsistent lead generation results",
      "High marketing spend with unclear returns",
      "Difficulty tracking customer acquisition costs"
    ],
    impact: "52% of businesses waste budget on solutions that address symptoms, not causes, leading to continued marketing inefficiency",
    quickWins: [
      "ROI tracking system implementation",
      "Marketing channel performance analysis",
      "Conversion rate optimization"
    ],
    auditBenefit: "The audit will analyze your entire customer acquisition funnel and identify which marketing investments will deliver the highest ROI.",
    framework: "Marketing & Customer Acquisition"
  },
  {
    id: 3,
    title: "Brand Perception & Digital Presence",
    description: "Customer first impressions directly impact conversion rates and long-term business success. Unclear or outdated branding costs you customers.",
    category: "Brand & Positioning",
    priority: "High",
    symptoms: [
      "Outdated or inconsistent brand presentation",
      "Poor website user experience",
      "Unclear value proposition",
      "Low digital engagement rates"
    ],
    impact: "Businesses with unclear brand positioning lose 23% of potential customers within the first 30 seconds of interaction",
    quickWins: [
      "Brand audit and positioning clarity",
      "Website user experience improvements",
      "Value proposition refinement"
    ],
    auditBenefit: "We'll analyze your digital touchpoints and brand positioning to ensure every customer interaction moves them closer to a purchase decision.",
    framework: "Brand & Digital Presence"
  },
  {
    id: 4,
    title: "Offer Clarity & Market Fit",
    description: "Confusing or multiple competing offers dilute your market message and reduce conversion rates across all marketing channels.",
    category: "Product & Offers",
    priority: "High",
    symptoms: [
      "Multiple unfocused service offerings",
      "Poor customer understanding of what you do",
      "Low conversion rates from inquiries",
      "Price objections and long sales cycles"
    ],
    impact: "Businesses with unclear offers experience 40% lower conversion rates and 60% longer sales cycles",
    quickWins: [
      "Core offer simplification",
      "Value proposition clarification",
      "Customer messaging alignment"
    ],
    auditBenefit: "The audit will help you identify your most profitable core offering and eliminate confusion that's costing you sales.",
    framework: "Product & Service Optimization"
  },
  {
    id: 5,
    title: "Systems & Process Optimization",
    description: "Inefficient systems create bottlenecks that limit your ability to scale and deliver consistent customer experiences.",
    category: "Operations",
    priority: "High",
    symptoms: [
      "High dependency on manual processes",
      "Frequent operational bottlenecks",
      "Inconsistent service delivery",
      "Team overwhelm and burnout"
    ],
    impact: "Poor systems and processes can reduce team productivity by up to 50% and create customer satisfaction issues",
    quickWins: [
      "Process automation opportunities",
      "Workflow optimization",
      "Task standardization"
    ],
    auditBenefit: "We'll identify your biggest operational inefficiencies and provide a roadmap to streamline operations for better scalability.",
    framework: "Operations & Systems"
  },
  {
    id: 6,
    title: "Team Alignment & Performance Management",
    description: "Unclear goals and accountability structures prevent your team from operating at full potential and achieving business objectives.",
    category: "Team & Leadership",
    priority: "Medium",
    symptoms: [
      "Team members unclear on priorities",
      "Lack of performance tracking",
      "Low team accountability",
      "Inconsistent results across team members"
    ],
    impact: "Teams without clear goals and accountability are 35% less productive and have 50% higher turnover rates",
    quickWins: [
      "Goal clarity and communication",
      "Performance tracking systems",
      "Accountability structures"
    ],
    auditBenefit: "The audit will reveal gaps in team alignment and provide frameworks for improving team performance and accountability.",
    framework: "Team & Human Resources"
  },
  {
    id: 7,
    title: "Customer Experience Optimization",
    description: "Poor customer experience leads to higher churn rates, reduced referrals, and missed growth opportunities through existing customers.",
    category: "Customer Experience",
    priority: "High",
    symptoms: [
      "High customer churn rates",
      "Few customer referrals",
      "Customer complaints and issues",
      "No systematic feedback collection"
    ],
    impact: "Businesses that don't measure customer experience lose 67% more customers annually and miss 80% of referral opportunities",
    quickWins: [
      "Customer feedback systems",
      "Experience touchpoint optimization",
      "Customer success processes"
    ],
    auditBenefit: "We'll analyze your customer journey and identify specific improvements that will increase satisfaction, retention, and referrals.",
    framework: "Customer Experience & Success"
  },
  {
    id: 8,
    title: "Performance Tracking & Analytics",
    description: "Without proper measurement systems, you're making decisions based on assumptions rather than data, leading to missed opportunities and wasted resources.",
    category: "Data & Analytics",
    priority: "Medium",
    symptoms: [
      "Decision-making based on gut feeling",
      "Lack of key performance indicators",
      "No regular performance reviews",
      "Inability to identify trends or patterns"
    ],
    impact: "Businesses without proper analytics make 45% more poor decisions and miss 60% of optimization opportunities",
    quickWins: [
      "KPI identification and tracking",
      "Performance dashboard creation",
      "Data-driven decision processes"
    ],
    auditBenefit: "The audit will establish baseline metrics and create a measurement framework that enables data-driven growth decisions.",
    framework: "Analytics & Measurement"
  },
  {
    id: 9,
    title: "Problem-Solving & Crisis Management",
    description: "Reactive problem-solving approaches prevent you from addressing root causes, leading to recurring issues and operational inefficiency.",
    category: "Strategic Management",
    priority: "Medium",
    symptoms: [
      "Recurring business problems",
      "Reactive rather than proactive approach",
      "Unclear problem-solving processes",
      "Issues escalating before being addressed"
    ],
    impact: "Reactive businesses spend 70% more time on problem-solving and have 3x higher operational costs",
    quickWins: [
      "Root cause analysis frameworks",
      "Proactive monitoring systems",
      "Problem escalation processes"
    ],
    auditBenefit: "We'll identify patterns in your business challenges and provide frameworks for proactive problem prevention and resolution.",
    framework: "Strategic Planning & Execution"
  },
  {
    id: 10,
    title: "Business Owner Clarity & Focus",
    description: "Feeling stuck or overwhelmed as a business owner often indicates a need for strategic clarity and prioritized action plans.",
    category: "Leadership & Strategy",
    priority: "Critical",
    symptoms: [
      "Feeling overwhelmed with business decisions",
      "Unclear prioritization of business activities",
      "Working harder without seeing results",
      "Lack of strategic direction"
    ],
    impact: "Business owners without clear strategic focus are 60% more likely to experience burnout and 40% less likely to achieve growth goals",
    quickWins: [
      "Strategic priority clarification",
      "Time and energy allocation optimization",
      "Decision-making frameworks"
    ],
    auditBenefit: "Our audit will provide the strategic clarity you need to focus on the activities that will have the biggest impact on your business growth.",
    framework: "Strategic Leadership & Vision"
  }
];
